import { useEffect, useMemo, useRef, useState } from 'react';

export interface GraphNode {
  id: string;
  kind: string;
  weight: number;
  health?: string;
}

export interface GraphEdge {
  source: string;
  target: string;
  weight: number;
}

interface SimNode extends GraphNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface Props {
  nodes: GraphNode[];
  edges: GraphEdge[];
  height?: number;
  onSelect?: (nodeId: string | null) => void;
}

export function NetworkGraph({ nodes, edges, height = 440, onSelect }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const simRef = useRef<SimNode[]>([]);
  const dragRef = useRef<{ id: string; ox: number; oy: number } | null>(null);

  const nodeIndex = useMemo(() => {
    const m = new Map<string, GraphNode>();
    for (const n of nodes) m.set(n.id, n);
    return m;
  }, [nodes]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const width = canvas.clientWidth || 800;
    canvas.width = width * devicePixelRatio;
    canvas.height = height * devicePixelRatio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

    const limitedNodes = [...nodes]
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 80);
    const ids = new Set(limitedNodes.map((n) => n.id));
    const limitedEdges = edges.filter((e) => ids.has(e.source) && ids.has(e.target)).slice(0, 200);

    simRef.current = limitedNodes.map((n, i) => {
      const angle = (i / Math.max(1, limitedNodes.length)) * Math.PI * 2;
      const r = 40 + Math.random() * Math.min(width, height) * 0.28;
      return {
        ...n,
        x: width / 2 + Math.cos(angle) * r,
        y: height / 2 + Math.sin(angle) * r,
        vx: 0,
        vy: 0,
      };
    });

    let frame = 0;
    let raf = 0;
    const tick = () => {
      frame++;
      const sim = simRef.current;
      const byId = new Map(sim.map((n) => [n.id, n]));

      // repulsion
      for (let i = 0; i < sim.length; i++) {
        for (let j = i + 1; j < sim.length; j++) {
          const a = sim[i];
          const b = sim[j];
          let dx = a.x - b.x;
          let dy = a.y - b.y;
          let dist = Math.hypot(dx, dy) || 0.01;
          const force = 1200 / (dist * dist);
          dx = (dx / dist) * force;
          dy = (dy / dist) * force;
          a.vx += dx;
          a.vy += dy;
          b.vx -= dx;
          b.vy -= dy;
        }
      }

      // springs
      for (const e of limitedEdges) {
        const a = byId.get(e.source);
        const b = byId.get(e.target);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy) || 0.01;
        const ideal = 90 + Math.min(80, e.weight * 4);
        const force = (dist - ideal) * 0.02 * Math.min(3, 0.5 + e.weight * 0.15);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }

      // center gravity + integrate
      for (const n of sim) {
        if (dragRef.current?.id === n.id) {
          n.vx = 0;
          n.vy = 0;
          continue;
        }
        n.vx += (width / 2 - n.x) * 0.005;
        n.vy += (height / 2 - n.y) * 0.005;
        n.vx *= 0.85;
        n.vy *= 0.85;
        n.x += n.vx;
        n.y += n.vy;
        n.x = Math.max(24, Math.min(width - 24, n.x));
        n.y = Math.max(24, Math.min(height - 24, n.y));
      }

      // draw
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(11,18,32,0.35)';
      ctx.fillRect(0, 0, width, height);

      for (const e of limitedEdges) {
        const a = byId.get(e.source);
        const b = byId.get(e.target);
        if (!a || !b) continue;
        const active = selected && (selected === a.id || selected === b.id);
        ctx.strokeStyle = active ? 'rgba(61,139,253,0.85)' : 'rgba(147,164,189,0.28)';
        ctx.lineWidth = active ? 2.2 : Math.min(3, 0.6 + e.weight * 0.25);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }

      for (const n of sim) {
        const r = n.kind === 'source' ? 10 + Math.min(10, Math.sqrt(n.weight)) : 6 + Math.min(8, Math.sqrt(n.weight));
        const isSel = selected === n.id || hover === n.id;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        if (n.kind === 'source') {
          ctx.fillStyle = n.health === 'error' ? '#e35d6a' : isSel ? '#7eb3ff' : '#3d8bfd';
        } else {
          ctx.fillStyle = isSel ? '#6af0a8' : '#2fbf71';
        }
        ctx.fill();
        if (isSel || n.weight > 8 || n.kind === 'source') {
          ctx.font = '12px IBM Plex Sans, sans-serif';
          ctx.fillStyle = '#e8eef8';
          ctx.fillText(n.id.length > 22 ? `${n.id.slice(0, 20)}…` : n.id, n.x + r + 4, n.y + 4);
        }
      }

      if (frame < 400 || dragRef.current) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [nodes, edges, height, selected, hover]);

  const pickNode = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    let best: SimNode | null = null;
    let bestDist = 18;
    for (const n of simRef.current) {
      const d = Math.hypot(n.x - x, n.y - y);
      const r = n.kind === 'source' ? 14 : 10;
      if (d < r + 4 && d < bestDist) {
        best = n;
        bestDist = d;
      }
    }
    return best;
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height, borderRadius: 10, cursor: 'grab', border: '1px solid rgba(148,163,184,0.14)' }}
        onMouseMove={(e) => {
          const n = pickNode(e.clientX, e.clientY);
          setHover(n?.id ?? null);
          if (dragRef.current && n === null) {
            // keep dragging selected
          }
          if (dragRef.current) {
            const canvas = canvasRef.current!;
            const rect = canvas.getBoundingClientRect();
            const node = simRef.current.find((x) => x.id === dragRef.current!.id);
            if (node) {
              node.x = e.clientX - rect.left;
              node.y = e.clientY - rect.top;
            }
          }
        }}
        onMouseDown={(e) => {
          const n = pickNode(e.clientX, e.clientY);
          if (n) {
            dragRef.current = { id: n.id, ox: n.x, oy: n.y };
            setSelected(n.id);
            onSelect?.(n.id);
          } else {
            setSelected(null);
            onSelect?.(null);
          }
        }}
        onMouseUp={() => {
          dragRef.current = null;
        }}
        onMouseLeave={() => {
          dragRef.current = null;
          setHover(null);
        }}
      />
      <div className="row muted" style={{ marginTop: 8, fontSize: '0.85rem' }}>
        <span className="badge run">bron</span>
        <span className="badge ok">@handle</span>
        <span>{nodeIndex.size} knopen · sleep om te verplaatsen · klik om te focussen</span>
        {selected && <strong style={{ color: 'var(--text)' }}>{selected}</strong>}
      </div>
    </div>
  );
}
